import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { Command, CommandPlugin } from '#ui-shared/components/modals/command-modal/command-modal.types';

export const useCommandModalStore = defineStore('commandModalStore', () => {
  const commandModals = ref<{ [key: string]: boolean }>({});
  const commands = ref<Command[]>([]);
  const commandPlugins = ref<CommandPlugin[]>([]);
  const lastToggleTime = ref<Date>();

  function removeDuplicateCommands(commands: Command[]): Command[] {
    const seenCommands = new Set<string>();
    return commands.filter((command) => {
      if (!seenCommands.has(command.id)) {
        seenCommands.add(command.id);
        return true;
      }
      return false;
    });
  }

  function removeDuplicatePlugins(
    commandPlugins: CommandPlugin[],
  ): CommandPlugin[] {
    const seenPlugins = new Set<string>();
    return commandPlugins.filter((plugin) => {
      if (!seenPlugins.has(plugin.id)) {
        seenPlugins.add(plugin.id);
        return true;
      }
      return false;
    });
  }

  function addCommands(commandsToAdd: Command[]): Command[] {
    commands.value = removeDuplicateCommands([
      ...commands.value,
      ...commandsToAdd,
    ]);

    return commands.value;
  }

  function clearCommands(): void {
    commands.value = [];
  }

  function removeCommandById(commandId: string): void {
    commands.value = commands.value.filter(
      currentCommand => currentCommand.id !== commandId,
    );
  }

  function removeCommand(command: Command): void {
    removeCommandById(command.id);
  }

  function addPlugins(pluginsToAdd: CommandPlugin[]): CommandPlugin[] {
    commandPlugins.value = removeDuplicatePlugins([
      ...commandPlugins.value,
      ...pluginsToAdd,
    ]);

    return commandPlugins.value;
  }

  function removePlugins(pluginsToRemove: CommandPlugin[]): void {
    const currentPlugins = commandPlugins.value;

    const updatedPlugins = currentPlugins.filter((currentPlugin) => {
      const isPluginToKeep = !pluginsToRemove.some((pluginToRemove) => {
        return pluginToRemove.id === currentPlugin.id;
      });

      return isPluginToKeep;
    });

    commandPlugins.value = updatedPlugins;
  }

  const isAnyCommandModalOpen = computed(() => {
    return Object.values(commandModals.value).some(modal => modal);
  });

  function mountCommandModal(
    modalId: string,
    commandPlugins?: CommandPlugin[],
  ): void {
    commandModals.value[modalId] = false;
    if (commandPlugins) {
      addPlugins(commandPlugins);
    }
  }

  function unmountCommandModal(modalId: string): void {
    delete commandModals.value[modalId];
  }

  function showCommandModal(modalId: string): boolean {
    if (!isAnyCommandModalOpen.value) {
      commandModals.value[modalId] = true;
      return true;
    }

    return false;
  }

  function hideAllCommandModals(): void {
    Object.keys(commandModals.value).forEach((modalId) => {
      commandModals.value[modalId] = false;
    });
  }

  function hideCommandModal(modalId: string): void {
    commandModals.value[modalId] = false;
  }

  function isCommandModalOpen(modalId: string): boolean {
    return commandModals.value[modalId] ?? false;
  }

  function isOtherCommandModalOpen(modalId: string): boolean {
    return Object.keys(commandModals.value).some(
      otherModalId =>
        otherModalId !== modalId && commandModals.value[otherModalId],
    );
  }

  function toggleCommandModal(modalId: string): boolean {
    if (isOtherCommandModalOpen(modalId)) {
      return false;
    }

    // Make sure we haven't toggled another modal recently.
    if (
      lastToggleTime.value
      && Date.now() - lastToggleTime.value.getTime() < 200
    ) {
      return false;
    }

    const didOpen = !commandModals.value[modalId];
    commandModals.value[modalId] = didOpen;
    lastToggleTime.value = new Date();
    return didOpen;
  }

  async function reloadCommands(): Promise<Command[]> {
    clearCommands();
    const commandList: Command[] = [];

    for (const plugin of commandPlugins?.value ?? []) {
      const pluginCommands = await plugin.getCommands();
      commandList.push(...pluginCommands);
    }

    return addCommands(commandList);
  }

  return {
    commands,
    commandModals,
    addCommands,
    addPlugins,
    reloadCommands,
    removeCommand,
    removeCommandById,
    removePlugins,
    isCommandModalOpen,
    isAnyCommandModalOpen,
    showCommandModal,
    hideCommandModal,
    mountCommandModal,
    unmountCommandModal,
    hideAllCommandModals,
    toggleCommandModal,
  };
});
